/*
 * How the Activity feed reads the rows a document writes (DV S-09 §3.5, Appendix F), and which of them
 * banner on the desktop: core's own kinds, so `coreRows.ts` hands them to the feed and the banner like
 * its other kinds. Their words are the document's, in `core/docs/i18n/shell.ts`. The server writes one payload per row — the document's key, its
 * name when the row was written, the path it opens at, and what the kind adds — and this file turns
 * it into one sentence of `i18n/shell.ts`. A payload this build cannot read gives "", and the feed
 * falls back to the kind's own title rather than a sentence with a hole in it.
 *
 * Claude's rows name Claude as "Claude, asked by Ben" and never banner: a steered Claude stays off
 * people's screens (S-09-D11). A visitor by a document's link has no account the feed could name, so
 * its row carries the name it wrote under, read "Max (visitor)" (DV W-1 D17, D26). A person's mention
 * banners, and so does a reply on the row of the thread's starter — the person who asked and waits
 * for the answer (S-09-D29).
 *
 * The file stays light: it is reached from the shell's chunk, so it imports nothing of the documents'
 * code and nothing of their own catalogue.
 */

import { formatDate } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import type { ActivityRowView } from "../modules/registry.ts"

/** A document row's payload, as the server writes it (`internal/core/docs/service_notify.go`). */
interface DocRowData {
	doc?: string
	title?: string
	path?: string
	in?: "comment" | "text"
	starter?: boolean
	count?: number
	by?: number[]
	outcome?: "accepted" | "rejected"
	before?: number
	all?: boolean
	undoUntil?: number
	bot?: boolean
	onBehalf?: number
	visitor?: { id: number; name?: string }
}

function read(data: unknown): DocRowData {
	return typeof data === "object" && data !== null ? data : {}
}

/** One document kind's reading: the feed's answers, its label, and whether a row banners. */
export interface DocRowView extends ActivityRowView {
	label: () => string
	/** Whether this row, of a kind that banners, is one the reader should see on the desktop. */
	banners?: (data: unknown) => boolean
}

type NameOf = (id: number) => string

/**
 * Who did it, as the row's sentence names them: the person, Claude asked by its asker, or a visitor
 * by link. `who` is the row's own person when the payload names one, else the row's actor.
 */
function actorName(
	row: DocRowData,
	actorIds: readonly number[],
	nameOf: NameOf,
	who?: number,
): string {
	if (row.bot === true) {
		const asker = row.onBehalf === undefined ? "" : nameOf(row.onBehalf)
		return asker === "" ? "" : t("doc.activity.byClaude", { asker })
	}
	if (row.visitor !== undefined) {
		const name = row.visitor.name ?? ""
		return name === "" ? "" : t("doc.activity.byVisitor", { name })
	}
	const id = who ?? actorIds[0]
	return id === undefined ? "" : nameOf(id)
}

const href = (data: unknown): string | null => read(data).path ?? null

/** The tag of a document's banners: one per document, so a busy document is one banner that updates. */
function tagOf(data: unknown): string {
	return `doc:${read(data).doc ?? ""}`
}

const mention: DocRowView = {
	label: () => t("doc.activity.mention.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		if (row.title === undefined) return ""
		const count = row.count ?? 1
		if (row.in === "text" && count > 1)
			return t("doc.activity.mention.lineTextMany", { count, doc: row.title })
		const name = actorName(row, actorIds, nameOf, row.by?.[0])
		if (name === "") return ""
		return row.in === "comment"
			? t("doc.activity.mention.lineComment", { name, doc: row.title })
			: t("doc.activity.mention.lineText", { name, doc: row.title })
	},
	href,
	desktop: { title: () => t("doc.activity.mention.title"), tag: tagOf },
	banners: (data) => read(data).bot !== true,
}

const reply: DocRowView = {
	label: () => t("doc.activity.reply.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		if (row.title === undefined) return ""
		const count = row.count ?? 1
		if (count > 1) return t("doc.activity.reply.lineMany", { count, doc: row.title })
		const name = actorName(row, actorIds, nameOf)
		return name === "" ? "" : t("doc.activity.reply.line", { name, doc: row.title })
	},
	href,
	// The starter's row coalesces apart from the thread's other reply rows (its own key), so a raise of
	// it is another person's answer to their question: it banners again.
	desktop: { title: () => t("doc.activity.reply.title"), tag: tagOf, onRaise: true },
	banners: (data) => read(data).starter === true,
}

const reaction: DocRowView = {
	label: () => t("doc.activity.reaction.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		if (row.title === undefined) return ""
		const count = row.count ?? 1
		if (count > 1) return t("doc.activity.reaction.lineMany", { count, doc: row.title })
		const name = actorName(row, actorIds, nameOf)
		return name === "" ? "" : t("doc.activity.reaction.line", { name, doc: row.title })
	},
	href,
}

const suggestion: DocRowView = {
	label: () => t("doc.activity.suggestion.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		const name = actorName(row, actorIds, nameOf, row.by?.[0])
		if (row.title === undefined || name === "") return ""
		return t("doc.activity.suggestion.line", { name, count: row.count ?? 1, doc: row.title })
	},
	href,
}

const decided: DocRowView = {
	label: () => t("doc.activity.decided.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		const name = actorName(row, actorIds, nameOf, row.by?.[0])
		if (row.title === undefined || name === "") return ""
		const args = { name, count: row.count ?? 1, doc: row.title }
		return row.outcome === "rejected"
			? t("doc.activity.decided.rejected", args)
			: t("doc.activity.decided.accepted", args)
	},
	href,
}

const historyCut: DocRowView = {
	label: () => t("doc.activity.historyCut.title"),
	line(data, actorIds, nameOf) {
		const row = read(data)
		const name = actorName(row, actorIds, nameOf)
		if (row.title === undefined || name === "") return ""
		const what =
			row.all === true || row.before === undefined
				? t("doc.activity.historyCut.lineAll", { name, doc: row.title })
				: t("doc.activity.historyCut.line", { name, doc: row.title, date: formatDate(row.before) })
		if (row.undoUntil === undefined) return what
		return `${what} · ${t("doc.activity.historyCut.undo", { date: formatDate(row.undoUntil) })}`
	},
	href,
}

/** kind → how the feed reads a document's row; the kinds `internal/core/docs` registers. */
export const DOC_ACTIVITY_ROWS: Readonly<Record<string, DocRowView>> = {
	"core.doc.mention": mention,
	"core.doc.reply": reply,
	"core.doc.reaction": reaction,
	"core.doc.suggestion": suggestion,
	"core.doc.decided": decided,
	"core.doc.history_cut": historyCut,
}
